import { useMemo, useState } from "react";
import { DEFAULT_PAGE_LENGTH } from "Util/Constants";
import { PaginationParams, SearchParams, SortingParams } from "./shared";

export type EntityListQueryOptions = {
	setSearchBy: (searchBy: string) => void;
	setSortBy: (sortBy: string) => void;
	setPageNo: (pageNo: number) => void;
	reset: () => void;
	changedSignature?: string; // signature of all the internal state variables to trigger a re-fetch
} & SearchParams & SortingParams & PaginationParams

export function useEntityListQueryParams(initialParams: Partial<EntityListQueryOptions> = {}): EntityListQueryOptions {
	// search functionality
	const [searchBy, setSearchBy] = useState(initialParams.searchBy || '');
	const [searchOn] = useState(initialParams.searchOn || []);
	
	// sorting functionality
	const [sortParams, _setSortParams] = useState<SortingParams>({
		sortBy: initialParams.sortBy || '',
		sortDescending: initialParams.sortDescending || false,
	});

	// pagination functionality
	const [pagination, _setPagination] = useState<PaginationParams>({
		pageNo: initialParams.pageNo || 0,
		pageSize: initialParams.pageSize || DEFAULT_PAGE_LENGTH,
	});

	const changedSignature = useMemo(() => {
		return JSON.stringify({
			...sortParams,
			...pagination,
			searchBy,
			searchOn,
		})
	}, [searchBy, searchOn, sortParams, pagination]);

	const setSortBy = (sortBy: string) => {
		_setSortParams({
			sortBy,
			sortDescending: sortParams.sortBy === sortBy ? !sortParams.sortDescending : false
		})
	};

	const setPageNo = (pageNo: number) => {
		_setPagination({
			...pagination,
			pageNo
		});
	}

	// reset to the initialParams
	const reset = () => {
		setSearchBy(initialParams.searchBy || '');
		_setSortParams({
			sortBy: initialParams.sortBy || '',
			sortDescending: initialParams.sortDescending || false
		});
		setPageNo(initialParams.pageNo || 0);
	}

	return {
		searchBy,
		searchOn,
		setSearchBy,
		sortBy: sortParams.sortBy,
		sortDescending: sortParams.sortDescending,
		setSortBy,
		pageNo : pagination.pageNo,
		pageSize: pagination.pageSize,
		setPageNo,
		reset,
		changedSignature
	};
}